import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { countryGeoImportStatus, runCountryGeoImport } from '../countryManagement.service';
import { resultMessage } from '../../../../service/apiResult';
import { primaryIconColor } from '../../../styles/iconStyles';

/** How often to re-ask while a load is running. Loads take minutes; a tighter loop buys nothing. */
const POLL_INTERVAL_MS = 3000;

const STATE_LABELS = {
  PENDING: 'Queued',
  RUNNING: 'Loading',
  COMPLETED: 'Loaded',
  FAILED: 'Failed',
};

function formatTimestamp(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString();
}

function StateChip({ state }) {
  if (!state) return null;
  const colour = state === 'FAILED' ? 'error' : state === 'COMPLETED' ? 'success' : 'warning';
  return <Chip size="small" color={colour} variant="outlined" label={STATE_LABELS[state] || state} />;
}

/**
 * Loads a country's administrative geography — its states, districts and blocks — from the reference
 * data held for that country.
 *
 * <p>Only rendered for a country that already exists: the load is keyed on the saved country's ISO
 * code, so there is nothing to load against on a country still being filled in.
 */
function GeographicalDataLoader({ countryId }) {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Guards every setState that can land after the component is gone — the poll timer and the two
  // async calls all outlive a navigation away from this screen.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const fetchStatus = useCallback(async () => {
    if (!countryId) return null;
    const result = await countryGeoImportStatus(countryId);
    if (!mounted.current) return null;

    if (!result.success) {
      setError(resultMessage(result, 'Could not read the geographical data status.'));
      setLoadingStatus(false);
      return null;
    }

    const next = result.data?.data || result.data || null;
    setStatus(next);
    setError(null);
    setLoadingStatus(false);
    return next;
  }, [countryId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Poll only while something is actually running, and stop the moment it is not — an interval left
  // running against a finished job is a request every three seconds for as long as the tab is open.
  useEffect(() => {
    if (!status?.importRunning) return undefined;
    const timer = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [status?.importRunning, fetchStatus]);

  const startImport = async () => {
    setConfirmOpen(false);
    setStarting(true);
    setError(null);

    const result = await runCountryGeoImport(countryId, false);
    if (!mounted.current) return;
    setStarting(false);

    if (!result.success) {
      setError(resultMessage(result, 'The load could not be started.'));
      return;
    }
    // Re-read rather than trusting the response body: a small country finishes inline and a large
    // one is only queued, and the status endpoint is the single place that knows which happened.
    fetchStatus();
  };

  const handleLoadClick = () => {
    if (status?.locationCount > 0) {
      setConfirmOpen(true);
      return;
    }
    startImport();
  };

  if (!countryId) return null;

  const lastRun = status?.lastRun;
  const running = Boolean(status?.importRunning);
  const canLoad = Boolean(status?.bundleAvailable) && !running && !starting;
  const expected = lastRun?.expectedLocations;
  const progressKnown = running && expected > 0;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px', borderColor: 'rgba(0, 0, 0, 0.12)' }}>
      {loadingStatus ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="textSecondary">
            Checking what is available…
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 320px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Icon icon="mdi:map-marker-multiple-outline" width="20" height="20" color={primaryIconColor} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Geographical Data
                </Typography>
                <StateChip state={lastRun?.state} />
              </Box>

              <Typography variant="body2" color="textSecondary">
                {status?.message || 'No information available.'}
              </Typography>

              {lastRun?.finishedAt && !running && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.75 }}>
                  Last load {formatTimestamp(lastRun.finishedAt)}
                  {lastRun.importedLocations != null && ` — ${lastRun.importedLocations} location(s) processed`}
                </Typography>
              )}
            </Box>

            <Button
              variant="contained"
              onClick={handleLoadClick}
              disabled={!canLoad}
              startIcon={
                running || starting
                  ? <CircularProgress size={16} sx={{ color: 'inherit' }} />
                  : <Icon icon="mdi:download-outline" width="18" height="18" />
              }
              sx={{
                minWidth: 168,
                height: 40,
                borderRadius: '12px',
                textTransform: 'none',
                fontSize: 15,
                fontWeight: 500,
              }}
            >
              {running || starting
                ? 'Loading…'
                : status?.locationCount > 0
                  ? 'Reload Data'
                  : 'Load Data'}
            </Button>
          </Box>

          {progressKnown && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.75 }}>
                Loading {expected} location(s). This continues in the background — you can leave this page.
              </Typography>
            </Box>
          )}

          {!status?.bundleAvailable && !running && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1.5 }}>
              There is no reference data on file for this country, so there is nothing to load.
              Its locations can still be entered by hand.
            </Typography>
          )}

          {error && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1.5 }}>
              {error}
            </Typography>
          )}
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Reload geographical data?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              This country already holds {status?.locationCount} location(s).
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Reloading <strong>updates those in place and adds anything new</strong> — it does not
              wipe and replace them. Nothing already recorded against a state, district or block —
              farmers, land, offices, coverage areas — is disturbed.
            </Typography>
            <Typography variant="body2">
              Locations that exist here but not in the reference data are left alone, and the names
              you have given this country's levels are not changed.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={startImport} variant="contained" sx={{ textTransform: 'none', borderRadius: '10px' }}>
            Reload
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default GeographicalDataLoader;
